import type { AlgoliaProductHit, ProductHit } from '../../../types/algolia';
import type { RecentlyViewedProduct } from '../../../utilities/helpers';
import type { SearchResponse } from '@algolia/client-search';
import type { BaseSyntheticEvent, MouseEvent } from 'react';
import type { InsightsMethodMap } from 'search-insights';

import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';

import { useEecProductClickEvent } from './useEecProductClickEvent';
import { useProductImpressionSectionName } from './useProductImpressionSectionName';
import { useProductImpressionTracking } from './useProductImpressionTracking';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { localisedData } from '../../../utilities/helpers';
import { QuickView } from '../../shared/quickView/QuickView';
import { Tile } from '../../shared/recommendations/tile/Tile';
import { useAlgoliaInsights } from '../../shared/vendors/useAlgoliaInsights';
import { WishlistActionType } from '~/components/pdp/productDetail/productDetailControl/WishlistActionType.enum';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { ALGOLIA_OBJECT_ID, PLP_NAVIGATED_PRODUCT } from '~/utilities/constants/localStorageKeys';
import { transformProduct } from '~/utilities/helpers/transformProduct';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';
import type { ProductClickViewType } from '~/utilities/vendors/dataLayer.type';

const WishlistAction = dynamic(
  async () =>
    (await import('~/components/pdp/productDetail/productDetailControl/WishlistAction'))
      .WishlistAction
);

export interface ProductTileProps {
  hit: AlgoliaProductHit;
  variants: SearchResponse<ProductHit>['hits'];
}

export const ProductTile = ({ hit, variants }: ProductTileProps) => {
  const [, setClickedStyleVariant] = useState(false);
  const { lang, cloudinaryURL1 } = useAppContext();
  const { systemTexts, isPLP } = useProductListContext();
  const {
    configuration: { defaultCurrency },
    priceFormatter,
  } = useStaticContext();

  const productToSolr = useCallback(
    () =>
      transformProduct({
        hit,
        lang,
        priceFormatter,
        defaultCurrency,
        cloudinaryURL1,
      }) as RecentlyViewedProduct,
    [cloudinaryURL1, defaultCurrency, hit, lang, priceFormatter]
  );

  const eecEventSectionName = useProductImpressionSectionName(hit);

  const { productTileRef } = useProductImpressionTracking({
    position: hit.__position,
    sectionName: eecEventSectionName,
    productToSolr,
  });

  const dispatchEecProductClick = useEecProductClickEvent({
    position: hit.__position,
    product: productToSolr(),
    sectionName: eecEventSectionName,
  });

  const { pushToAlgoliaWithQueryId } = useAlgoliaInsights();
  const { primaryIndex } = useAlgoliaSortingOptions();
  const algoliaEventNamePrefix: string = isPLP ? 'PLP' : 'PRP';

  const preventDefault = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const pushAlgoliaEvents = useCallback(
    (sourceEventName?: string) => {
      const event: keyof InsightsMethodMap = 'clickedObjectIDsAfterSearch';
      const eventName: string =
        sourceEventName || `${algoliaEventNamePrefix}_clickedObjectIDsAfterSearch`;

      pushToAlgoliaWithQueryId(event, eventName, {
        objectIDs: [hit.objectID],
        positions: [hit.__position],
        queryID: hit.__queryID,
      });
    },
    [algoliaEventNamePrefix, hit, pushToAlgoliaWithQueryId]
  );

  const handleVariantLinkClick = useCallback(() => {
    setClickedStyleVariant(true);

    pushAlgoliaEvents(`${algoliaEventNamePrefix}_STYLEVAR_clickedObjectIDsAfterSearch`);
  }, [pushAlgoliaEvents, algoliaEventNamePrefix]);

  const sendEventCallback = useCallback(
    (viewType: ProductClickViewType, notPushAlgoliaEvent = false) => {
      setClickedStyleVariant(prevState => {
        if (!prevState && !notPushAlgoliaEvent) {
          pushAlgoliaEvents();
        }

        return false;
      });

      dispatchEecProductClick(viewType);
    },
    [pushAlgoliaEvents, setClickedStyleVariant, dispatchEecProductClick]
  );

  const handleLinkClick = useCallback(
    (e: BaseSyntheticEvent) => {
      sendEventCallback('general view', e.target?.attributes?.['data-is-quickview']);
    },
    [sendEventCallback]
  );

  const handleQuickViewPushEvent = useCallback(() => {
    sessionStorage?.setItem(PLP_NAVIGATED_PRODUCT, hit.code);
    sessionStorage?.setItem(ALGOLIA_OBJECT_ID, hit.objectID);
    localStorage?.setItem('algoliaQueryID', hit.__queryID!);
    localStorage?.setItem('algoliaIndexName', primaryIndex!);

    sendEventCallback('quick view');
  }, [sendEventCallback, hit, primaryIndex]);

  return (
    <Tile
      ref={productTileRef}
      productPrice={systemTexts?.productPrice}
      tileType="plp"
      product={hit}
      variants={variants}
      showVariantSelector
      availableColorsLabel={systemTexts?.availableColours}
      signingEnabled
      onVariantLinkClick={handleVariantLinkClick}
      onLinkClick={handleLinkClick}
      index={hit.__position - 1}
      quickView={
        <QuickView
          code={hit['baseProduct.code']}
          productName={localisedData(hit, hit.name, lang.toUpperCase())}
          algoliaAddToCartEventName={`${algoliaEventNamePrefix}_addedToCartObjectIDs`}
          algoliaAddToCartAfterSearchEventName={`${algoliaEventNamePrefix}_addedToCartObjectIDsAfterSearch`}
          pushClickEvent={handleQuickViewPushEvent}
        />
      }
      wishlistAction={
        <FeatureFlag flag="enableWishlist">
          <WishlistAction
            wishlistButtonType={WishlistActionType.Tile}
            productCode={hit['baseProduct.code'] || hit.code}
            productName={localisedData(hit, hit.name, lang.toUpperCase())}
            onClick={preventDefault}
          />
        </FeatureFlag>
      }
    />
  );
};
